import { getModuleTypeLabel } from "@/lib/module-type-label";
import type {
  HandyoutSectionKey,
  ModuleChangeType,
  ModuleRevisionBatch,
  ModuleRevisionChange,
} from "@/types/handyout-modules";
import {
  Redo2,
  RotateCcw,
  Sparkles,
  Undo2,
  User,
  WandSparkles,
  type LucideIcon,
} from "lucide-react";

const SECTION_LABELS: Record<HandyoutSectionKey, string> = {
  intro: "Intro",
  toc: "Table of contents",
  "key-takeaways": "Key takeaways",
  faq: "FAQ",
  feedback: "Feedback",
};

const EDITOR_CHANGE_ORDER: readonly ModuleChangeType[] = [
  "insert",
  "delete",
  "upsert",
  "reorder",
  "slide_order",
];

const EDITOR_CHANGE_LABELS: Record<ModuleChangeType, [string, string]> = {
  insert: ["Module added", "Modules added"],
  delete: ["Module deleted", "Modules deleted"],
  upsert: ["Module edited", "Modules edited"],
  reorder: ["Module moved", "Modules reordered"],
  slide_order: ["Slides reordered", "Slides reordered"],
};

export interface HistoryEntry {
  batchId: string;
  label: string;
  icon: LucideIcon;
  time: string;
  modules: string[];
  places: string[];
  detail: string;
}

function primaryChanges(batch: ModuleRevisionBatch): ModuleRevisionChange[] {
  for (const kind of EDITOR_CHANGE_ORDER) {
    const matching = batch.changes.filter((change) => change.change_type === kind);
    if (matching.length > 0) return matching;
  }
  return batch.changes;
}

function editorLabel(batch: ModuleRevisionBatch): string {
  const primary = primaryChanges(batch);
  const kind = primary[0]?.change_type;
  if (!kind) return "Change";
  const [single, plural] = EDITOR_CHANGE_LABELS[kind];
  return primary.length > 1 ? plural : single;
}

export function historyBatchLabel(batch: ModuleRevisionBatch): string {
  switch (batch.source) {
    case "ai_accept":
      return "AI changes accepted";
    case "ai_fill":
      return "Filled with AI";
    case "pipeline":
      return "Generated";
    case "migration":
      return "Initial version";
    case "restore":
      return "Version restored";
    case "undo":
      return "Undo";
    case "redo":
      return "Redo";
    default:
      return editorLabel(batch);
  }
}

export function historyBatchIcon(batch: ModuleRevisionBatch): LucideIcon {
  switch (batch.source) {
    case "ai_accept":
    case "ai_fill":
      return Sparkles;
    case "pipeline":
    case "migration":
      return WandSparkles;
    case "restore":
      return RotateCcw;
    case "undo":
      return Undo2;
    case "redo":
      return Redo2;
    default:
      return User;
  }
}

function isModuleChange(change: ModuleRevisionChange): boolean {
  return change.change_type !== "slide_order" && change.module_id !== null;
}

function changeName(change: ModuleRevisionChange): string {
  if (change.section && change.section !== "slide") {
    return SECTION_LABELS[change.section] ?? getModuleTypeLabel(change.type);
  }
  const title = typeof change.title === "string" ? change.title.trim() : "";
  return title || getModuleTypeLabel(change.type);
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values));
}

export function formatHistoryTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

export function buildHistoryEntries(
  batches: readonly ModuleRevisionBatch[],
  slideNumbers: ReadonlyMap<string, number>,
): HistoryEntry[] {
  return batches.map((batch) => {
    const moduleChanges = batch.changes.filter(isModuleChange);
    const shown = (batch.source === "editor" ? primaryChanges(batch) : batch.changes).filter(
      isModuleChange,
    );
    const modules = unique(shown.map(changeName));
    const slideNumbersInBatch = unique(
      moduleChanges
        .filter((change) => change.section === "slide")
        .map((change) => {
          const number = change.slide_id ? slideNumbers.get(change.slide_id) : undefined;
          return number != null ? `Slide ${number}` : "Removed slide";
        }),
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const places = moduleChanges.some((change) => change.section !== "slide")
      ? [...slideNumbersInBatch, "General modules"]
      : slideNumbersInBatch;
    return {
      batchId: batch.batch_id,
      label: historyBatchLabel(batch),
      icon: historyBatchIcon(batch),
      time: formatHistoryTime(batch.updated_at || batch.created_at),
      modules,
      places,
      detail: [places.join(", "), modules.join(", ")].filter(Boolean).join(" · "),
    };
  });
}
